// The Booking cross-roster pipeline (`/roster`, booking subview) — its OWN screen, sibling to the Label
// RosterHomeScreen and the Management RosterHeartbeatScreen, dispatched from RosterHomeRoute on the
// SERVER-derived org subview.
//
// It was a conditional arm INSIDE RosterHomeScreen until 2026-09-01, which is why a booking agent's pipeline
// carried the Label header ("Roster, this week") and the Label's born-blank takeover. Splitting it is the same
// shape the Management lens already had: one screen per lens, each owning its header, its four states and its
// section order. The composed read is shared (`useRosterOverview`, one query key), so the split costs no fetch.
//
// MONEY-BEARING via `overview.booking` — see BookingRosterOverview for the section-by-section notes.
//
// "+ New offer" lives HERE (ART-139), in two consistent places — the shell HEADER action and the Active-deals
// card's own button — both opening the same band PICKER. The create then happens RIGHT HERE on the all-roster
// page (the agent never leaves it, PO): an offer must be born ON an artist (PO, 2026-09-02), so the picker
// names one and `NewOfferModal` is mounted under a transient org-lens band context for it. On save we
// `refetch()` the overview directly — the query object is right here — so the Offer column shows the new row.
// The orchestration lives on the SCREEN, not the overview body, because the header action needs it too.
//
// The page title stands ALONE. Legacy prints a "«org» / Booking overview" crumb above it; that kind of
// kicker over an h1 was ruled out by the product owner on 2026-08-17 (the category label was repeating what
// the nav already said) and `headings.guard.test.ts` enforces it. The org is named by the rail and the
// account chip either way, which is the reasoning that ruling rests on.
import { useState } from "react";
import { Plus } from "lucide-react";
import { AsOfMarker, Button } from "@cntrl/ui";
import { useOrgContext } from "../../app/orgContext.js";
import { useRosterOverview, useRosterArtists, useRosterNeedsAttention } from "../../app/hooks/useRoster.js";
import { NeedsAttentionCard } from "./NeedsAttentionCard.js";
import { RosterBandProvider } from "../../shells/roster/RosterBandProvider.js";
import { BookingRosterOverview } from "./BookingRosterOverview.js";
import { NewOfferBandPicker } from "./NewOfferBandPicker.js";
import { NewOfferModal } from "./NewOfferModal.js";
import { RosterDealModal, type RosterDealTarget } from "./RosterDealModal.js";
import { RosterScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import styles from "./BookingRoster.module.scss";

/**
 * The header's roster line. Legacy's two forms verbatim — "N artists · this season" once anything is
 * rostered, "0 artists · no season yet" for a fresh org — because "this season" is a claim about work in
 * flight, and an org with nobody on it has no season to be in.
 */
function seasonLine(artistCount: number): string {
  if (artistCount === 0) return "0 artists · no season yet";
  return `${artistCount} ${artistCount === 1 ? "artist" : "artists"} · this season`;
}

export function BookingRosterScreen({ onNavigate }: { onNavigate?: (route: string) => void }) {
  const { orgId } = useOrgContext();
  const overview = useRosterOverview(orgId);
  const attention = useRosterNeedsAttention(orgId);
  const data = overview.data;
  const artistCount = data?.header?.artistCount ?? 0;
  const lastRefresh = data?.header?.lastRefresh;
  const loaded = !overview.isLoading && !!data;

  // "+ New offer" flow. `pickingBand` shows the artist picker; `offerForBand` is the chosen band the create
  // form is mounted under (via a transient RosterBandProvider — the scope shell has no band context of its
  // own). The roster list is the same reach-scoped read the rail uses, deferred until the picker opens.
  const [pickingBand, setPickingBand] = useState(false);
  const [offerForBand, setOfferForBand] = useState<string | null>(null);
  const artists = useRosterArtists(pickingBand ? orgId : undefined);
  // The card the agent opened — its record shows OVER this page and closing returns here (ART-139).
  const [dealTarget, setDealTarget] = useState<RosterDealTarget | null>(null);

  // Loading / error only. NO born-blank takeover, deliberately: an unassigned booking staffer still gets the
  // full page with every section in its own empty state, which is what the design's first-run specimen
  // shows. The Label home replaces itself with "No artists yet" because its cards are all roster
  // aggregates; a booking agent's page is a set of named stages, and a stage that is absent rather than
  // empty is indistinguishable from a stage nobody built. So this lens never returns `empty` — which the
  // shell's state union allows for exactly this reason.
  const state: RosterScreenState = overview.isLoading
    ? { status: "loading" }
    : overview.isError || !data?.booking
      ? { status: "error", error: overview.error, onRetry: () => void overview.refetch() }
      : { status: "ready" };

  return (
    <RosterScreenShell
      title="Booking pipeline"
      sub={
        <>
          {/* The roster line waits for the read. "0 artists · no season yet" is a definite claim about the
              org, and rendering it off a not-yet-loaded count states it to every booking agent for the
              length of the fetch — the born-blank page and the loading page must never look alike. */}
          {loaded && <span>{seasonLine(artistCount)}</span>}
          {/* The shared staleness primitive, which renders nothing without a stamp. Legacy printed a bare
              "last sync," with nothing after it whenever the roster had never settled a show. */}
          {loaded && <AsOfMarker asOf={lastRefresh} />}
          <span className={styles.vcBadge}>+ VC coming Fall</span>
        </>
      }
      // The header action, shown once the page is ready — the SAME picker the Active-deals card opens, so the
      // affordance reads the same in both places (PO, ART-139).
      actions={
        loaded ? (
          <Button size="sm" variant="ghost" onClick={() => setPickingBand(true)}>
            <Plus size={16} aria-hidden /> New offer
          </Button>
        ) : undefined
      }
      state={state}
      // Taller than the default: this page leads with a triage list over a kanban board, not a KPI strip.
      skeletonHeight={420}
    >
      {/* Guarded, not asserted: JSX children are CONSTRUCTED at this call site whatever state the shell
          decides to draw, so `data!.booking!` dereferences on the very first render. */}
      {/* Needs attention (ART-324) — back on this page by the product owner's later ruling, which reverses
          ART-140's removal: one row per artist, their most pressing booking item (an expiring hold, a played
          show not settled, an opportunity). */}
      {data?.booking && (
        <NeedsAttentionCard
          scope="roster"
          items={attention.data?.items}
          total={attention.data?.total}
          isLoading={attention.isLoading}
          canOpenShows
          onOpenArtist={(bandId) => onNavigate?.(`/roster/artists/${bandId}`)}
          onEdited={() => {
            void attention.refetch();
            void overview.refetch();
          }}
        />
      )}
      {data?.booking && (
        <BookingRosterOverview
          booking={data.booking}
          onNavigate={onNavigate}
          onNewOffer={() => setPickingBand(true)}
          onOpenDeal={setDealTarget}
        />
      )}

      {/* A clicked card's record, OVER this page. Closing returns here AND refetches the overview, so an edit
          (a confirmed deal, a released hold, a converted/passed offer) is reflected without a manual refresh. */}
      <RosterDealModal
        target={dealTarget}
        onClose={() => {
          setDealTarget(null);
          void overview.refetch();
        }}
        onNavigate={onNavigate}
      />

      {/* Pick the artist (offers are born on one), then create right here — no navigation. */}
      {pickingBand && (
        <NewOfferBandPicker
          artists={artists.data ?? []}
          onSelect={(bandId) => {
            setPickingBand(false);
            setOfferForBand(bandId);
          }}
          onClose={() => setPickingBand(false)}
        />
      )}

      {/* The create form under a transient org-lens band context for the chosen band. On close we refetch the
          overview directly (the query object is right here), so the new offer shows in the Offer column
          without a manual refresh. */}
      {offerForBand && (
        <RosterBandProvider artistId={offerForBand}>
          <NewOfferModal
            onClose={() => {
              setOfferForBand(null);
              void overview.refetch();
            }}
          />
        </RosterBandProvider>
      )}
    </RosterScreenShell>
  );
}
